import type {
    DigitallyReceivedPrescriberOrderBasicInfoFragment,
    UnclaimedPrescriberOrderInfoFragment,
} from '../../graphql/generated/graphql';

import type { BasicPrescriberOrderFragment } from './prescriber-order';

type DateKeys =
    | keyof DigitallyReceivedPrescriberOrderBasicInfoFragment
    | keyof UnclaimedPrescriberOrderInfoFragment;

/**
 * Sorts an array of medication orders (dRx or non-dRx) by a specified date field
 *
 * @param orders - The array of medication orders to sort
 * @param sortKey - Optional string key to override default sort field
 * @returns The sorted array of medication orders
 */
export const sortOrdersByDate = (
    orders: BasicPrescriberOrderFragment[],
    sortKey?: DateKeys
): BasicPrescriberOrderFragment[] => {
    const getDate = (med: BasicPrescriberOrderFragment): Date => {
        if (sortKey && sortKey in med) {
            const value = (med as Record<typeof sortKey, string | undefined>)[
                sortKey
            ];
            return new Date(value ?? 0);
        }

        switch (med.__typename) {
            case 'DigitallyReceivedPrescriberOrder':
                return new Date(med.writtenDate ?? 0);
            case 'UnclaimedPrescriberOrder':
                return new Date(med.writtenDateUnclaimed ?? 0);
            default:
                return new Date(0);
        }
    };

    return orders.sort((a, b) => getDate(b).getTime() - getDate(a).getTime());
};
